import React from 'react';
import './ScoopMarker.css';

/**
 * The scoop status marker - the brand's signature two-tint sphere on a tan
 * cone triangle. One HTML template is the single source of truth so the
 * Leaflet divIcon (needs an HTML string) and React list rows render the
 * exact same DOM.
 *
 * Brand rule: the scoop is ALWAYS a full sphere = two halves of the same
 * colour (lighter top, darker bottom, hard 50/50 split). Never a lone half.
 */

/* Two-tint pairs per status variant (top = lighter, bottom = base).
   Light tint = base mixed ~38% towards white (same recipe as --fou-scoop-red). */
export const SCOOP_VARIANTS = {
    green:     { top: '#98CBA5', bottom: '#55A86A' },  /* beschikbaar / onderweg */
    red:       { top: '#F8838B', bottom: '#F4303C' },  /* niet beschikbaar */
    tan:       { top: '#DDC29F', bottom: '#C79A63' },  /* gereserveerd */
    scheduled: { top: '#C6B7A4', bottom: '#8C7359' },  /* gepland (nog niet gestart) */
};

const RESERVATION_STATUSES = ['EN_ROUTE_TO_ICECREAM_VAN_RESERVATION', 'AT_ICECREAM_VAN_RESERVATION'];

/* Non-colour status glyph per variant (accessibility): pause = unavailable,
   clock = reservation, available = none. */
const STATUS_GLYPH_SVG = {
    red: '<svg viewBox="0 0 12 12" width="9" height="9"><rect x="2.5" y="2" width="2.5" height="8" rx="1" fill="#fff"/><rect x="7" y="2" width="2.5" height="8" rx="1" fill="#fff"/></svg>',
    tan: '<svg viewBox="0 0 12 12" width="9" height="9"><circle cx="6" cy="6" r="5" fill="none" stroke="#fff" stroke-width="1.4"/><path d="M6 3.2V6l1.9 1.1" stroke="#fff" stroke-width="1.4" fill="none" stroke-linecap="round"/></svg>',
};

/**
 * Map a kar (truck) to its scoop colour variant.
 * Derived ONLY from kar.status + kar.available (fields the marker already
 * re-renders on - see TruckMarker.shouldComponentUpdate).
 */
export function scoopVariantForKar(kar) {
    if (kar?.status === 'SCHEDULED') return 'scheduled';
    if (RESERVATION_STATUSES.includes(kar?.status)) return 'tan';
    return kar?.available ? 'green' : 'red';
}

/**
 * Build the scoop marker HTML string.
 * @param {'green'|'red'|'tan'} variant
 * @param {string|number} number - truck number shown inside the scoop
 * @param {object} opts - { size: sphere diameter px, withCone: render tan triangle below }
 */
export function scoopMarkerHTML(variant, number, { size = 36, withCone = true } = {}) {
    const colors = SCOOP_VARIANTS[variant] || SCOOP_VARIANTS.red;
    const n = String(number ?? '').replace(/\D/g, '');
    /* Cone proportions from the brandmark: base ~0.87 x diameter, top starts
       below the sphere equator so the triangle flows out cleanly. */
    const coneW = Math.round(size * 0.87);
    const coneH = Math.round(size * 0.87);
    const totalH = withCone ? Math.round(size * 0.75) + coneH : size;

    const cone = withCone
        ? `<span class="scoop-cone" style="border-left-width:${coneW / 2}px;border-right-width:${coneW / 2}px;border-top-width:${coneH}px;top:${Math.round(size * 0.75)}px;left:${Math.round((size - coneW) / 2)}px;"></span>`
        : '';

    // Non-colour status cue (accessibility): available has none, unavailable
    // gets a pause glyph, reservation gets a clock glyph - so the meaning
    // doesn't depend on red/green/tan alone. Placed OUTSIDE the (overflow-
    // hidden) sphere, anchored to the sphere's lower-right.
    const glyphSvg = STATUS_GLYPH_SVG[variant];
    const badge = glyphSvg
        ? `<span class="scoop-badge" aria-hidden="true" style="left:${size - 15}px;top:${size - 15}px;">${glyphSvg}</span>`
        : '';

    return (
        `<span class="scoop-marker scoop-${variant}${withCone ? ' scoop-with-cone' : ''}" style="width:${size}px;height:${totalH}px;">` +
        cone +
        `<span class="scoop-sphere" style="width:${size}px;height:${size}px;">` +
        `<span class="scoop-half scoop-top" style="background:${colors.top};"></span>` +
        `<span class="scoop-half scoop-bottom" style="background:${colors.bottom};"></span>` +
        (n ? `<span class="scoop-number">${n}</span>` : '') +
        `</span>` +
        badge +
        `</span>`
    );
}

/**
 * React wrapper around the same HTML (used in TruckCard rows, legends, ...).
 */
export function ScoopMarker({ variant = 'red', number = '', size = 28, withCone = false, className = '' }) {
    return (
        <span
            className={`scoop-marker-wrap ${className}`}
            aria-hidden="true"
            dangerouslySetInnerHTML={{ __html: scoopMarkerHTML(variant, number, { size, withCone }) }}
        />
    );
}

export default ScoopMarker;
